import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCompressArrowsAlt, faExpandArrowsAlt } from "@fortawesome/free-solid-svg-icons";
import { Button } from "aible";
import { useDispatch, useSelector } from "types/store";
import { isEnabled } from "utils/featureFlags";
import { getIsKPIAnalysis, copyAndChangeDatasetTarget, getDataset, getDefaultSuccess, getIsOutcomeMulticategorical, setDatasetTarget, updateDatasetOptions, getIsKPIReady } from "containers/Home/reducer";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import DatasetOptionsInput from "components/UnifiedProject/SelectData/DatasetOptions";
import { DataReadinessTooltip } from "components/Tiles/Sense/DataReadinessPanel";
import TargetIcon from "images/SenseIcons/bullseye-light 1.svg";
import TelescopeIcon from "images/SenseIcons/Telescope Duotone Light.svg";
import styles from "./style.module.css";
import { getIsOpenOutcome, getIsOpenTargetModelOptions, getOutcome, getIsLoadingChat } from "containers/Sense/dataset-selectors";
import { updateTileState } from "containers/Sense/reducer";
import { handleSelectUseCase, initKPIAnalysis } from "containers/ChatGPT/reducer";
import { updateAgentAutonomyView } from "containers/View/reducer";
import * as RelationUtils from "utils/relation";
import { TargetInput } from "./Relation/TargetInput";
import * as nextReducer from "@/containers/Next/NextReducer";
const TargetModelOptionsPanel = ({ datasetId }) => {
    const dispatch = useDispatch();
    const options = useSelector((state) => getDataset(state, datasetId)?.options ?? {});
    const labels = useSelector((state) => getDataset(state, datasetId)?.labels ?? []);
    const targetModelOption = !options.target_steps?.includes("SHAP_EXPLORE")
        ? "legacy"
        : options.target_steps?.includes("BIGQUERY_MODEL")
            ? "bigQuery"
            : options.target_model_params === undefined
                ? "default"
                : "custom";
    return (React.createElement("div", null,
        React.createElement("div", { className: styles.shapLabelWrapper, onClick: () => {
                const steps = (options.target_steps ?? []).filter((s) => s != "BIGQUERY_MODEL");
                if (!steps.includes("SHAP_EXPLORE")) {
                    steps.push("SHAP_EXPLORE");
                }
                dispatch(updateDatasetOptions(datasetId, {
                    ...options,
                    target_model_params: undefined,
                    target_steps: steps
                }));
            } },
            React.createElement("input", { type: "radio", name: "targetModelOption", value: "default", checked: targetModelOption === "default" }),
            React.createElement("span", { className: styles.shapLabel }, "Use Default Settings")),
        React.createElement("div", { className: styles.shapLabelWrapper, onClick: () => {
                const steps = (options.target_steps ?? []).filter((s) => s != "BIGQUERY_MODEL");
                if (!steps.includes("SHAP_EXPLORE")) {
                    steps.push("SHAP_EXPLORE");
                }
                dispatch(updateDatasetOptions(datasetId, {
                    ...options,
                    target_model_params: {},
                    target_steps: steps
                }));
            } },
            React.createElement("input", { type: "radio", name: "targetModelOption", value: "default", checked: targetModelOption === "custom" }),
            React.createElement("span", { className: styles.shapLabel }, "Custom Settings")),
        targetModelOption === "custom" && (React.createElement(React.Fragment, null,
            React.createElement(DatasetOptionsInput, { options: options, labels: labels, onChange: (o) => dispatch(updateDatasetOptions(datasetId, {
                    ...options,
                    ...o
                })) })))));
};
const OutcomePanel = ({ datasetId }) => {
    const dispatch = useDispatch();
    const dataset = useSelector((state) => getDataset(state, datasetId));
    const updateIsOpenOutcome = (isOpenOutcome) => {
        dispatch(updateTileState(dataset.id, { isOpenOutcome }));
    };
    const updateIsTargetModelOptionsOpen = (isOpenTargetModelOptions) => {
        dispatch(updateTileState(dataset.id, { isOpenTargetModelOptions }));
    };
    const outcomeState = useSelector((state) => getOutcome(state, dataset.id));
    const isOpenOutcome = useSelector((state) => getIsOpenOutcome(state, dataset.id));
    const isTargetModelOptionsOpen = useSelector((state) => getIsOpenTargetModelOptions(state, dataset.id));
    const isKPIAnalysis = useSelector((state) => getIsKPIAnalysis(state, dataset.id));
    const showKpiOverview = useSelector((state) => getIsKPIReady(state, dataset.id));
    const isProcessing = dataset?.status_name !== "DONE";
    const isWideDatasetWarning = dataset?.wideDatasetWarning;
    const isExpandable = dataset?.outcome && !isProcessing && !isWideDatasetWarning;
    const isRetargeting = outcomeState.isLoadingRetarget;
    const selOutcome = outcomeState.datasetOutcome;
    const isMulticategorical = useSelector((state) => getIsOutcomeMulticategorical(state, dataset.id));
    const selectedOutcomeIsMulticategorical = useSelector((state) => getIsOutcomeMulticategorical(state, dataset.id, undefined, selOutcome));
    const defaultSuccess = useSelector((state) => getDefaultSuccess(state, dataset.id));
    let isSelOutcomeMulticategorical = false;
    if (selOutcome !== undefined && selectedOutcomeIsMulticategorical) {
        isSelOutcomeMulticategorical = true;
    }
    const isLoadingChat = useSelector((state) => getIsLoadingChat(state, datasetId));
    const datasetContext = RelationUtils.getOutputContext(RelationUtils.fromDataset(dataset));
    const currentNextId = useSelector((state) => nextReducer.getCurrentNextId(state)) ?? "new";
    const nextId = datasetId ? "DATASET_" + datasetId : currentNextId;
    const comparisonOptions = useSelector((state) => nextReducer.getComparisonOptions(state, nextId));
    const field = dataset?.fields?.[comparisonOptions?.dateField];
    const dates = field?.data || [];
    const maxOrder = dataset.options?.target_model_params?.data?.combination_order;
    const isThirdOrderAnalysis = maxOrder === 3;
    const handleTargetChange = (val) => {
        dispatch(updateTileState(dataset.id, {
            outcome: {
                isLoadingRetarget: outcomeState.isLoadingRetarget,
                datasetOutcome: val,
                outcomeSuccess: undefined
            }
        }));
    };
    const handleTargetValueChange = (val) => {
        dispatch(updateTileState(dataset.id, {
            outcome: {
                isLoadingRetarget: outcomeState.isLoadingRetarget,
                datasetOutcome: outcomeState.datasetOutcome,
                outcomeSuccess: {
                    label: String(val),
                    value: val
                }
            }
        }));
    };
    return isProcessing && dataset?.outcome === undefined ? (React.createElement("div", null)) : (React.createElement("div", { id: "outcomeSection_" + dataset?.id, className: isOpenOutcome || !isExpandable ? styles.openOverview : styles.overview, style: {
            width: "calc(100% - 20px)",
            // zIndex: 200,
            maxHeight: isOpenOutcome || !isExpandable ? "1600px" : "42px"
        }, onClick: isOpenOutcome !== true && isExpandable ? () => updateIsOpenOutcome(!isOpenOutcome) : undefined }, dataset?.outcome ? (isProcessing ? (React.createElement("div", { className: styles.oTitle, style: { width: "calc(100% - 20px)", display: "flex" } },
        React.createElement(LoadingIndicator, { style: { margin: "0px", marginRight: "24px", marginLeft: "-8px", transform: "scale(.6)" } }),
        React.createElement("span", null, "Evaluating"),
        React.createElement("span", { style: { fontWeight: "bold", marginLeft: "3px", overflow: "hidden", textOverflow: "ellipsis" } }, dataset?.outcome))) : (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.senseTitle },
            React.createElement("div", { className: styles.sTitle, style: { width: "calc(100% - 20px)" } },
                React.createElement("span", { className: styles.sIcon, style: isThirdOrderAnalysis ? { marginRight: "26px", position: "relative" } : undefined },
                    React.createElement("img", { src: isThirdOrderAnalysis ? TelescopeIcon : TargetIcon, style: isThirdOrderAnalysis
                            ? {
                                height: "20px",
                                width: "20px",
                                position: "absolute",
                                top: "0px",
                                transform: "translateY(-50%)"
                            }
                            : { height: "16px", width: "16px" } })),
                React.createElement("span", null, "Evaluating"),
                React.createElement("span", { style: {
                        fontWeight: "bold",
                        marginLeft: "3px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: isMulticategorical ? "228px" : undefined
                    } }, dataset?.outcome),
                isMulticategorical && (React.createElement(React.Fragment, null,
                    React.createElement("span", { style: { marginLeft: "3px" } }, "is"),
                    React.createElement("span", { style: {
                            fontWeight: "bold",
                            marginLeft: "3px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "25%",
                            whiteSpace: "nowrap"
                        } }, defaultSuccess)))),
            isOpenOutcome ? (React.createElement("div", { id: "outcomeSectionClose_" + dataset?.id, className: styles.closeButton, onClick: () => updateIsOpenOutcome(!isOpenOutcome) },
                React.createElement(Button, { style: { padding: "3px" } },
                    React.createElement("div", { className: styles.selIcon, title: "Close" },
                        React.createElement(FontAwesomeIcon, { icon: faCompressArrowsAlt }))))) : (React.createElement("div", { className: styles.selIcon + " " + styles.expandIcon },
                React.createElement(FontAwesomeIcon, { icon: faExpandArrowsAlt })))),
        isOpenOutcome && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: styles.outcomeBodyText, style: { overflow: "hidden", textOverflow: "ellipsis" } }, `You are viewing the data readiness analysis for ${dataset?.outcome}. If you would like to evaluate a different variable in this dataset, select it below and Aible will create a new analysis card for the outcome`),
            React.createElement(TargetInput, { context: datasetContext, target: selOutcome, onTargetChange: handleTargetChange, targetValue: outcomeState.outcomeSuccess?.value, onTargetValueChange: handleTargetValueChange, isRetargeting: isRetargeting }),
            React.createElement(Button, { className: styles.outcomeReselectButton, onClick: () => {
                    dispatch(updateTileState(dataset.id, {
                        outcome: {
                            isLoadingRetarget: true,
                            datasetOutcome: outcomeState.datasetOutcome,
                            outcomeSuccess: outcomeState.outcomeSuccess
                        }
                    }));
                    const promiseHelper = () => {
                        dispatch(updateTileState(dataset.id, {
                            isOpenOutcome: !isOpenOutcome,
                            outcome: {
                                isLoadingRetarget: false,
                                datasetOutcome: undefined,
                                outcomeSuccess: undefined
                            }
                        }));
                    };
                    if (outcomeState.datasetOutcome) {
                        dispatch(copyAndChangeDatasetTarget(dataset?.id, outcomeState.datasetOutcome, outcomeState.outcomeSuccess?.value)).finally(promiseHelper);
                    }
                }, disabled: (!isSelOutcomeMulticategorical && outcomeState.datasetOutcome === undefined) ||
                    (isSelOutcomeMulticategorical && outcomeState.outcomeSuccess === undefined) ||
                    isRetargeting, type: "primary" }, isRetargeting ? (React.createElement(LoadingIndicator, { color: "#333", style: {
                    height: "13px",
                    width: "13px",
                    margin: "0px",
                    position: "absolute",
                    transform: "scale(0.4)",
                    top: "0px",
                    left: "8px"
                } })) : ("Create New Data Readiness Analysis")),
            isEnabled("light-gbm-shap-explore") && (React.createElement("div", { style: { marginTop: "10px", fontSize: "13px" } },
                React.createElement("a", { style: { marginTop: "5px", color: "#7E69D7" }, onClick: () => updateIsTargetModelOptionsOpen(!isTargetModelOptionsOpen) }, isTargetModelOptionsOpen ? "Hide Advanced Options" : "Show Advanced Options"))),
            isTargetModelOptionsOpen && React.createElement(TargetModelOptionsPanel, { datasetId: dataset.id })))))) : (React.createElement("div", null,
        React.createElement("div", { className: styles.senseTitle, style: { alignItems: "center", lineHeight: "16px" } },
            React.createElement("div", { className: styles.sIcon },
                React.createElement("img", { src: TargetIcon, style: { height: "16px", width: "16px" } })),
            React.createElement("div", { className: styles.nFlex },
                "Select an outcome to evaluate (optional)",
                React.createElement(DataReadinessTooltip, { dataset: dataset, isOnlyInfoCircle: true }))),
        React.createElement("div", { className: styles.outcomeBodyText }, `This dataset is ready to use. If you would like to analyze what is impacting the ` +
            `behavior of a specific KPI please select that business outcome below (optional)`),
        React.createElement("div", { style: { width: "90%", marginTop: "5px", fontSize: "13px" } },
            React.createElement(TargetInput, { context: datasetContext, target: selOutcome, onTargetChange: handleTargetChange, targetValue: outcomeState.outcomeSuccess?.value, onTargetValueChange: handleTargetValueChange, isRetargeting: isRetargeting })),
        React.createElement("div", { style: {
                marginTop: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start"
            } },
            React.createElement(Button, { onClick: () => {
                    if (outcomeState.datasetOutcome) {
                        dispatch(setDatasetTarget(dataset?.id, outcomeState.datasetOutcome, isSelOutcomeMulticategorical ? outcomeState.outcomeSuccess?.value : undefined));
                    }
                }, style: { marginRight: "10px" }, disabled: outcomeState.datasetOutcome === undefined ||
                    (isSelOutcomeMulticategorical && outcomeState.outcomeSuccess === undefined), type: "primary" }, "Evaluate Data Readiness"),
            isEnabled("document-set-kpi-analysis") && isKPIAnalysis && showKpiOverview && (React.createElement(Button, { onClick: () => {
                    dispatch(initKPIAnalysis(dataset?.id));
                }, disabled: dates.length <= 1, style: {
                    minWidth: "90px",
                    minHeight: "40px",
                    padding: "9px 14px",
                    marginRight: "10px"
                }, isLoading: isLoadingChat }, "What's Changed Overview")),
            isEnabled("chat-aible") && (React.createElement(Button, { onClick: () => {
                    if (isEnabled("agent-autonomy-controls")) {
                        dispatch(updateAgentAutonomyView("RECOMMENDED"));
                    }
                    dispatch(handleSelectUseCase("CHATAIBLE_SQL", dataset?.id));
                }, style: {
                    minWidth: "90px",
                    minHeight: "40px",
                    padding: "9px 14px",
                    marginRight: "10px"
                }, isLoading: isLoadingChat === true }, "Data Query (NLQ) Chat")),
            isEnabled("light-gbm-shap-explore") && (React.createElement("a", { onClick: () => updateIsTargetModelOptionsOpen(!isTargetModelOptionsOpen), style: { fontSize: "13px", color: "#7E69D7", margin: "10px 0px" } }, isTargetModelOptionsOpen ? "Hide Advanced Options" : "Show Advanced Options"))),
        React.createElement("div", null, isTargetModelOptionsOpen && React.createElement(TargetModelOptionsPanel, { datasetId: dataset.id }))))));
};
export default OutcomePanel;
